import { Download, Mail, ArrowDown } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { useContent } from "@/lib/useContent";

const STATS = [
    { k: "02", v: "Episodes live" },
    { k: "05+", v: "Generations in conversation" },
    { k: "01", v: "City at the heart: Bombay" },
    { k: "100%", v: "Queer-owned & independent" },
];

export default function Press() {
    const { settings, press_assets } = useContent();

    const scrollToAssets = () => {
        document.getElementById("press-assets-section")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div data-testid="press-page">
            <section className="bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20">
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        For the curious and the curators
                    </p>
                    <h1
                        data-testid="press-heading"
                        className="mt-5 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl"
                    >
                        <MaskedLine delay={0.1}>BOMGAY,</MaskedLine>
                        <MaskedLine delay={0.25} className="italic text-pink">
                            on the record.
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.4}>
                        <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-ink/80">
                            Writing about us, exploring a collaboration or looking for a
                            conversation worth having? Find the essentials here, including
                            podcast information, the host biography, approved photographs,
                            brand assets and contact details.
                        </p>
                        <button
                            data-testid="press-explore-btn"
                            onClick={scrollToAssets}
                            className="mt-8 inline-flex items-center gap-2 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                        >
                            Explore the Press Kit <ArrowDown className="w-4 h-4" />
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* Stats */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="press-stats">
                    {STATS.map((s, i) => (
                        <Reveal key={s.k} delay={i * 0.08}>
                            <div
                                className={`bg-cream border-2 border-ink hard-shadow-sm p-6 ${
                                    i % 2 ? "rotate-1" : "-rotate-1"
                                } transition-transform duration-300 hover:rotate-0`}
                            >
                                <p className="font-display font-black text-3xl sm:text-4xl text-pink">
                                    {s.k}
                                </p>
                                <p className="mt-2 font-sub font-bold text-sm text-ink">
                                    {s.v}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Downloads */}
            <section id="press-assets-section" className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28">
                <Reveal>
                    <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
                        Brand assets &amp; artwork
                    </h2>
                    <p className="mt-3 text-sm text-ink/70">
                        Click any asset to open it full-size, then save it. Credit: BOMGAY.in.
                    </p>
                </Reveal>
                {press_assets.length === 0 ? (
                    <p data-testid="press-assets-empty" className="mt-10 font-sub font-bold text-ink/60">
                        Assets are being gathered, check back soon.
                    </p>
                ) : (
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {press_assets.map((a, i) => (
                            <Reveal key={a.id} delay={i * 0.06}>
                                <div
                                    data-testid={`press-asset-${a.id}`}
                                    className={`group bg-cream border-2 border-ink hard-shadow-sm ${
                                        i % 2 ? "rotate-1" : "-rotate-1"
                                    } transition-transform duration-300 hover:rotate-0`}
                                >
                                    <div className="border-b-2 border-ink overflow-hidden">
                                        <img
                                            src={a.url}
                                            alt={a.label}
                                            className="w-full aspect-square object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                                        />
                                    </div>
                                    <div className="p-5 flex items-center justify-between gap-4">
                                        <div>
                                            <p className="font-sub font-bold text-ink">{a.label}</p>
                                            <p className="text-xs text-ink/60 mt-1">{a.note}</p>
                                        </div>
                                        <a
                                            href={a.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`press-download-${a.id}`}
                                            aria-label={`Download ${a.label}`}
                                            className="shrink-0 p-3 bg-pink text-cream border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                                        >
                                            <Download className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
            </section>

            {/* Contact */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                                Media contact
                            </p>
                            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight leading-tight">
                                Interviews, features &amp; collabs
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-ink/80">
                                Sagar is available for interviews on queer media, Bombay's
                                LGBTQIA+ history, podcasting in India and the making of
                                BOMGAY.
                            </p>
                            <a
                                href={`mailto:${settings.contact_email}`}
                                data-testid="press-email-link"
                                className="mt-6 inline-flex items-center gap-2 font-sub font-bold text-pink underline underline-offset-4 break-all"
                            >
                                <Mail className="w-4 h-4 shrink-0" /> {settings.contact_email}
                            </a>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <SubmissionForm
                            idPrefix="press-form"
                            lockKind="press"
                            heading="Press inquiry"
                            blurb="Tell us your outlet, deadline and what you need. We'll get back fast."
                        />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
